import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import { cn } from "../lib/cn";

export interface FabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Announced in place of the icon, which carries no text. Required — not optional. */
  label: string;
  icon: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The one always-reachable action on a screen.
 *
 * The pale sage accent rather than the brand ink: it floats over the content, and the
 * design system reserves the accent for exactly this kind of single, unmissable mark.
 *
 * Takes the shell log button's place on a phone: the shell hides its own while a Fab
 * is on screen (see index.css), so there is one round button, not two stacked.
 */
export function Fab({ className, label, icon, ref, ...props }: FabProps) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      data-fab=""
      className={cn(
        "absolute right-5 z-20 flex size-14 items-center justify-center rounded-full",
        "bottom-[calc(var(--spacing-navbar)+var(--nav-bottom-gap,var(--spacing-navbar-inset))+var(--safe-area-bottom)+var(--spacing)*4)]",
        "md:bottom-[calc(var(--spacing-lg)+var(--safe-area-bottom))]",
        // The pale sage fill, with dark text on it. `bg-secondary` is the terracotta
        // text value and would paint this an alarm.
        "bg-secondary-container text-on-secondary-container shadow-raised",
        "transition-transform hover:scale-105",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed-dim",
        className,
      )}
      {...props}
    >
      <span aria-hidden>{icon}</span>
    </button>
  );
}
